/**
 * Preset avatar gallery ids — MUST stay in sync with velaro-messaging's
 * server/Velaro.Messaging.Data/Constants/PresetAvatars.cs and
 * client/src/constants/logos.ts PRESET_AVATARS. Add new entries only; never
 * reuse/renumber an existing id, since it may already be stored on a
 * WorkspaceUser.Picture row.
 *
 * velaro-admin has no server-side copy of this list on purpose: setting an
 * avatar goes through velaro-messaging's own `POST Profile/SelectPresetAvatar`
 * (see AvatarPickerModal.tsx), which is the sole place that validates the id
 * and writes WorkspaceUser.Picture. Duplicating that validation here would be
 * a second, unenforced copy of the same rule — this list is UI-only, used to
 * render the gallery and to build display URLs.
 */
export const PRESET_AVATAR_IDS: readonly string[] = [
  "illustrated-1",
  "illustrated-2",
  "illustrated-3",
  "illustrated-4",
  "illustrated-5",
  "illustrated-6",
  "illustrated-7",
  "illustrated-8",
  "illustrated-9",
  "illustrated-10",
  "illustrated-11",
  "illustrated-12",
  "illustrated-13",
  "illustrated-14",
  "illustrated-15",
  "illustrated-16",
  "illustrated-17",
  "illustrated-18",
  "illustrated-19",
  "illustrated-20",
  "photo-1",
  "photo-2",
  "photo-3",
  "photo-4",
  "photo-5",
  "photo-6",
  "photo-7",
  "photo-8",
];

/** Same shape as velaro-messaging's PresetAvatars.BuildUrl. */
export function buildPresetAvatarUrl(messagingClientBaseUrl: string, id: string): string {
  return `${(messagingClientBaseUrl ?? "").replace(/\/+$/, "")}/img/avatars/avatar-${id}.jpg`;
}

/**
 * Mirrors velaro-messaging's client/src/components/SquareAvatar.tsx
 * DEFAULT_HEADSHOT_PATTERN — detects "this Picture is just the generic
 * placeholder" so the picker can show "No avatar selected yet" instead of
 * highlighting a preset that isn't actually selected.
 */
export const DEFAULT_HEADSHOT_PATTERN = /\/headshots\/default\.png$/i;

export function isGenericDefaultPicture(pictureUrl: string | null | undefined): boolean {
  return !pictureUrl || DEFAULT_HEADSHOT_PATTERN.test(pictureUrl);
}

/** Extracts the preset id from a Picture URL previously built by buildPresetAvatarUrl, or null. */
export function extractPresetAvatarId(pictureUrl: string | null | undefined): string | null {
  if (!pictureUrl) return null;
  const match = pictureUrl.match(/avatar-([a-z0-9-]+)\.jpg(?:\?.*)?$/i);
  if (!match) return null;
  const id = match[1];
  return PRESET_AVATAR_IDS.includes(id) ? id : null;
}
